import { Users } from 'lucide-react';

import { PersonAvatar } from '@/components/PersonAvatar';
import { MultiPopover } from '@/components/ui/multi-popover';
import { projectMemberName } from '../select';
import type { ProjectMember, WorkItem } from '../types';
import { AssigneeAvatars } from './AssigneeAvatars';

type Person = NonNullable<WorkItem['assignee_details']>[number];

/**
 * The work-item Assignees picker — one look in the create dialog and the
 * detail drawer. Lists the project's members with their avatar; the trigger
 * shows the first assignee's name (+N) and their avatars.
 */
export function MemberPicker({
  members,
  selected,
  onToggle,
  onClear,
  known = [],
  loading = false,
}: {
  /** The project's members — the only people who can be assigned. */
  members: ProjectMember[];
  selected: number[];
  onToggle: (id: number) => void;
  onClear: () => void;
  /** People already resolved elsewhere (the item's `assignee_details`), so the
   *  trigger can name an assignee before the member list loads or after they
   *  left the project. */
  known?: Person[];
  /** The member list is still loading — say so instead of "no members". */
  loading?: boolean;
}) {
  const people = new Map<number, Person>(known.map((p) => [p.id, p]));
  for (const pm of members) {
    people.set(pm.member, {
      id: pm.member,
      name: pm.member_name ?? people.get(pm.member)?.name ?? projectMemberName(pm),
      photo: pm.member_photo ?? people.get(pm.member)?.photo ?? null,
    });
  }

  const chosen = selected.flatMap((id) => people.get(id) ?? []);
  // An assignee who has since left the project keeps a row, so they can be
  // unassigned one by one — not only with Clear.
  const memberIds = new Set(members.map((pm) => pm.member));
  const listed = [
    ...members.map((pm) => people.get(pm.member)!),
    ...chosen.filter((p) => !memberIds.has(p.id)),
  ];
  const options = listed.map((person) => ({
    id: person.id,
    name: person.name,
    leading: <PersonAvatar name={person.name} photo={person.photo} />,
  }));

  return (
    <MultiPopover
      icon={Users}
      label="Assignees"
      count={selected.length}
      options={options}
      selected={selected}
      onToggle={onToggle}
      onClear={onClear}
      emptyText={loading ? 'Loading members…' : 'No project members yet.'}
      preview={
        chosen.length > 0 ? (
          <span className="flex items-center gap-1.5">
            <span className="max-w-[12rem] truncate text-sm font-normal">
              {chosen[0].name}
              {selected.length > 1 ? ` +${selected.length - 1}` : ''}
            </span>
            <AssigneeAvatars assignees={chosen} max={3} />
          </span>
        ) : undefined
      }
    />
  );
}
